import React, { useEffect, useRef, useState } from 'react';
import ChatApp from '../ChatApp';
import { ChatPlatformProvider, useChatPlatform } from '../context/ChatPlatformContext';
import { useSessionsActions } from '../store/hooks';
import IsolatedRouter from './IsolatedRouter';
import { STORAGE_KEYS } from './config/modes';
import { useDockExpansion } from './shell/DockExpansionContext';
import { getSessionIdByKey, setSessionIdByKey } from './utils/sessionKeyCache';

const readInitialSessionId = () => {
    if (typeof window === 'undefined') return null;
    try {
        return window.localStorage.getItem(STORAGE_KEYS.LAST_SESSION) || null;
    } catch {
        return null;
    }
};

function SessionsBootstrap({ cacheKey }) {
    const { activeSessionId, setIsRightPanelOpen, isLoading } = useChatPlatform();
    const { fetch: fetchSessions } = useSessionsActions();
    const { setDockExpanded } = useDockExpansion();

    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);

    useEffect(() => {
        try {
            if (activeSessionId) {
                if (cacheKey) {
                    setSessionIdByKey(cacheKey, activeSessionId);
                } else {
                    localStorage.setItem(STORAGE_KEYS.LAST_SESSION, activeSessionId);
                }
            } else if (!cacheKey) {
                localStorage.removeItem(STORAGE_KEYS.LAST_SESSION);
            }
        } catch {
            // ignore
        }
    }, [activeSessionId, cacheKey]);

    useEffect(() => {
        setIsRightPanelOpen(false);
    }, [activeSessionId, setIsRightPanelOpen]);

    const expandInitRef = useRef(true);
    useEffect(() => {
        if (expandInitRef.current) {
            expandInitRef.current = false;
            return;
        }
        if (activeSessionId || isLoading) setDockExpanded(true);
    }, [activeSessionId, isLoading, setDockExpanded]);

    return null;
}

export default function ChatAssistantRouter({ children, forceNewSession, cacheKey }) {
    const [initialSessionId, setInitialSessionId] = useState(() => {
        if (cacheKey) return undefined;
        return forceNewSession ? null : readInitialSessionId();
    });

    useEffect(() => {
        if (!cacheKey) return;
        getSessionIdByKey(cacheKey).then(id => setInitialSessionId(id ?? null));
    }, []);

    if (initialSessionId === undefined) return null;

    return (
        <IsolatedRouter>
            <ChatPlatformProvider
                persistRightPanel={false}
                initialActiveSessionId={initialSessionId}
                initialIsEmbedded={true}
            >
                <SessionsBootstrap cacheKey={cacheKey} />
                {children}
                <div className="flex-1 min-h-0 relative flex">
                    <ChatApp />
                </div>
            </ChatPlatformProvider>
        </IsolatedRouter>
    );
}
